import { ROUTES } from '@/constants'
import { dateHelper } from '@/helpers/dateHelper'
import { i18n } from '@/plugins/i18n'
import BookingService from '@/services/booking/BookingService'
import { SET_IS_LOADING_APP, SET_LANGUAGE } from '@/store/mutation-types'
import { Booking, ContactType, ClientPage, Delivery, HeaderContent } from '@/types'
import Vue from 'vue'

export default Vue.extend({
  data: () => ({
    availableLanguages: [] as string[],
    clientPage: null as ClientPage | null,
    headerData: null as HeaderContent | null,
    booking: null as Booking | null,
    delivery: null as Delivery | null,
    language: null as string | null,
  }),
  methods: {
    async getBooking(type: string, language?: string): Promise<void> {
      try {
        const {
          params: { id },
        } = this.$route

        if (!language) {
          language = this.$store.getters.language
        }

        this.language = language || null

        const { booking, languages, pages } = await BookingService.getBooking(id, type, language)

        const {
          retailer,
          createdAt,
          customer: { timezone },
          Delivery: currentDelivery,
          lockerCarrier,
          orderId,
        } = booking

        this.headerData = {
          retailerLogo: retailer.logo,
          carrierLogo: currentDelivery?.carrier.logo,
          carrierName: currentDelivery?.carrier.name,
          lockerLogo: lockerCarrier?.logo,
          lockerName: lockerCarrier?.name,
          referenceNumber: orderId,
          createdAt,
          timezone,
        }
        this.clientPage = pages[0]
        this.booking = booking
        this.delivery = currentDelivery

        if (languages) {
          this.availableLanguages = languages!
          this.$root.$i18n.i18next.language = this.clientPage!.language

          i18n.changeLanguage(this.clientPage!.language)
          dateHelper.changeLocale(this.clientPage!.language)
          // This force update of i18n system to allow the use of this.$t in methods and computeds
          this.$root.$i18n.i18nLoadedAt = dateHelper.getDate().toString()

          this.$store.commit(SET_LANGUAGE, this.clientPage!.language)
        }

        if (!languages.includes(language)) {
          this.$store.commit(SET_LANGUAGE, this.clientPage!.language)
        }

        this.$store.commit(SET_IS_LOADING_APP, false)
      } catch (e) {
        this.$router.push({ name: ROUTES.NOT_FOUND })
      }

      return
    },
  },
  computed: {
    showCarrierName(): boolean {
      if (this.clientPage?.config.carrierInfos?.showCarrierName === undefined) {
        return true
      }
      return !!this.clientPage?.config.carrierInfos?.showCarrierName
    },
    showCarrierLogo(): boolean {
      return !!this.clientPage?.config.carrierInfos?.showCarrierLogo
    },
    showClaimUrl(): boolean {
      return !!this.clientPage?.config.carrierInfos?.showClaimUrl
    },
    showCarrierEmail(): boolean {
      if (this.clientPage?.config.carrierInfos) {
        if (this.clientPage?.config.carrierInfos.email.type === ContactType.CARRIER) {
          return this.delivery?.carrier.contact?.email !== undefined
        }

        return this.clientPage?.config.carrierInfos.email.type !== ContactType.EMPTY
      }
      return false
    },
    showCarrierPhone(): boolean {
      if (this.clientPage?.config.carrierInfos) {
        if (this.clientPage?.config.carrierInfos?.phone.type === ContactType.CARRIER) {
          return this.delivery?.carrier.contact?.phone !== undefined
        }
        return this.clientPage?.config.carrierInfos?.phone.type !== ContactType.EMPTY
      }
      return false
    },
    carrierEmail(): string | undefined {
      if (this.clientPage?.config.carrierInfos?.email.type === ContactType.CUSTOM) {
        return this.clientPage?.config.carrierInfos?.email.value
      }
      return this.delivery?.carrier.contact?.email
    },
    carrierPhone(): string | undefined {
      if (this.clientPage?.config.carrierInfos?.phone.type === ContactType.CUSTOM) {
        return this.clientPage?.config.carrierInfos?.phone.number
      }
      return this.delivery?.carrier.contact?.phone
    },
    showCarrierInfosCard(): boolean {
      return (
        this.delivery !== null &&
        (this.showCarrierName ||
          this.showCarrierLogo ||
          this.showClaimUrl ||
          this.showCarrierEmail ||
          this.showCarrierPhone)
      )
    },
    showLockerLogo(): boolean {
      return !!this.clientPage?.config.lockerInfos?.showLockerLogo
    },
    showLockerName(): boolean {
      return !!this.clientPage?.config.lockerInfos?.showLockerName
    },
    showLockerEmail(): boolean {
      if (this.clientPage?.config.lockerInfos) {
        if (this.clientPage?.config.lockerInfos.email.type === ContactType.CUSTOM) {
          return this.delivery?.carrier.contact?.email !== undefined
        }
        return this.clientPage?.config.lockerInfos.email.type !== ContactType.EMPTY
      }
      return false
    },
    showLockerPhone(): boolean {
      if (this.clientPage?.config.lockerInfos) {
        if (this.clientPage?.config.lockerInfos?.phone.type === ContactType.CUSTOM) {
          return this.delivery?.carrier.contact?.phone !== undefined
        }
        return this.clientPage?.config.lockerInfos?.phone.type !== ContactType.EMPTY
      }
      return false
    },
    lockerEmail(): string | undefined {
      if (this.clientPage?.config.lockerInfos?.email.type === ContactType.CUSTOM) {
        return this.clientPage?.config.lockerInfos?.email.value
      }
    },
    lockerPhone(): string | undefined {
      if (this.clientPage?.config.lockerInfos?.phone.type === ContactType.CUSTOM) {
        return this.clientPage?.config.lockerInfos?.phone.number
      }
    },
    showLockerInfosCard(): boolean {
      return (
        this.booking !== null &&
        (this.showLockerName || this.showLockerLogo || this.showLockerEmail || this.showLockerPhone)
      )
    },
  },
})
